import React from 'react';

export interface MaintenanceModeProviderProps {
  children: React.ReactNode;
  apiURL: string;
  headers?: Record<string, string>;
}

export interface ContextProviderProps {
  isEnabled: boolean;
  enableMaintenanceMode: () => void;
  disableMaintenanceMode: () => void;
}

export const MaintenanceModeContext = React.createContext<ContextProviderProps>(
  {
    isEnabled: false,
    enableMaintenanceMode: () => {},
    disableMaintenanceMode: () => {},
  },
);

export const useMaintenanceMode = (): ContextProviderProps =>
  React.useContext(MaintenanceModeContext);

interface Polling {
  interval: ReturnType<typeof setInterval> | null;
}

const DEFAULT_RETRY_AFTER_SECONDS = 60;

export const MaintenanceModeProvider: React.FunctionComponent<
  MaintenanceModeProviderProps
> = ({ children, apiURL, headers }) => {
  const [isEnabled, setIsEnabled] = React.useState<boolean>(false);

  // Set synchronously, so calls made before the next render see it. Each call
  // to enableMaintenanceMode owns one polling object, and its callbacks bail
  // out once that object is no longer the current one.
  const pollingRef = React.useRef<Polling | null>(null);

  const stopPolling = React.useCallback(() => {
    if (pollingRef.current?.interval) {
      clearInterval(pollingRef.current.interval);
    }
    pollingRef.current = null;
  }, []);

  const enableMaintenanceMode = React.useCallback(() => {
    if (pollingRef.current) return;

    const polling: Polling = { interval: null };
    pollingRef.current = polling;
    setIsEnabled(true);

    const isCurrent = () => pollingRef.current === polling;

    const finish = () => {
      if (!isCurrent()) return;
      stopPolling();
      setIsEnabled(false);
    };

    const startPolling = (timeInSeconds: number) => {
      if (!isCurrent()) return;

      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log({
          message: `Maintenance mode enabled. Will retry in ${timeInSeconds} seconds`,
        });
      }

      polling.interval = setInterval(() => {
        fetch(apiURL || '', { headers })
          .then((innerResponse) => {
            if (innerResponse.status !== 503) finish();

            if (process.env.NODE_ENV !== 'production') {
              // eslint-disable-next-line no-console
              console.log({
                time: new Date().toLocaleTimeString(),
                message: 'Did fetch',
                status: innerResponse.status,
              });
            }
          })
          // Network failure: keep polling until the API answers
          .catch(() => {});
      }, timeInSeconds * 1000);
    };

    fetch(apiURL || '', { headers })
      .then((response) => {
        if (response.status !== 503) {
          finish();
          return;
        }

        startPolling(
          Number(response.headers.get('Retry-After')) ||
            DEFAULT_RETRY_AFTER_SECONDS,
        );
      })
      // The request that triggered this got a 503, so keep the screen and
      // poll instead of leaving it stuck without anything to clear it
      .catch(() => startPolling(DEFAULT_RETRY_AFTER_SECONDS));
  }, [apiURL, headers, stopPolling]);

  const disableMaintenanceMode = React.useCallback(() => {
    stopPolling();
    setIsEnabled(false);
  }, [stopPolling]);

  React.useEffect(() => stopPolling, [stopPolling]);

  const context = React.useMemo<ContextProviderProps>(
    () => ({
      isEnabled,
      enableMaintenanceMode,
      disableMaintenanceMode,
    }),
    [disableMaintenanceMode, enableMaintenanceMode, isEnabled],
  );

  return (
    <MaintenanceModeContext.Provider value={context}>
      {children}
    </MaintenanceModeContext.Provider>
  );
};
